import { useEffect, useState } from "react";
import "./Support.scss";
import images from "../../assets/images";
import CustomTextField from "./components/CustomTextField";
import ButtonStyle from "../../components/ButtonStyle/ButtonStyle";
import StandardTextarea from "./components/CustomTextArea";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { messages, patterns, PHONE_NUMBER_TEXT } from "../../utils/constants";
import {formatPhoneNumber, getScreenDimensions} from '../../utils/helper';
import { useMutation } from "@apollo/client";
import { CONTACT_ADMIN } from "./api/Support";
import ToasterWithMessage from "../../components/toaster";
import axios, { AxiosResponse } from "axios";
import Loader from "../../components/Loader/Loader";
import { useNavigate } from "react-router-dom";

interface IFormInput {
  name: string;
  phoneNumber: string;
  email: string;
  message: string;
}

interface ContactInfo {
  title: string;
  content: string;
  icon: string;
}

interface RawContactInfo {
  "Contact Title": string;
  Address: string;
  "Phone Number": string;
  Emails: string;
  Policy: string;
}

const schema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required(messages?.name)
    // .matches(patterns?.filenameAtleast1Alpha, messages?.validName)
    .max(100, "Name should not exceed 100 characters"),
  email: yup
    .string()
    .trim()
    .matches(
      patterns.email,
      messages.validEmail
    )
    .required(messages?.email),
  phoneNumber: yup
    .string()
    .trim()
    .required("Phone number is required")
    .test("is-valid-phoneNumber", messages.validPhoneNumber, function (value) {
      const { path, createError } = this;
      const startsWithPlus = value.startsWith("+");
      console.log(value, "value");
  
      if (!patterns.phoneNumber.test(value)) {
        return createError({ path, message: messages.validPhoneNumber });
      }
      // Check length based on whether it starts with +
      if (startsWithPlus && value.length > 20) {
        return createError({ path, message: messages.phoneNumberLength });
      } else if (!startsWithPlus && value.length > 16) {
        return createError({ path, message: messages.phoneNumberLength16 });
      }
      return true;
  }),

  message: yup.string().trim().required(messages?.messages),
});

const Support = () => {
  const { Location, Phone, Envelope, LegalBackIcon } = images;
  const { success, error } = ToasterWithMessage();
  const katon_cms = process.env.REACT_APP_KATON_CMS_SITE;
  const [rawData, setRawData] = useState<RawContactInfo[]>();
  const [contactInfo, setContactInfo] = useState<ContactInfo[]>([]);
  const [loader, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isMobileScreen, setIsMobileScreen] = useState(getScreenDimensions().isMobileScreen);

  useEffect(() => {
    const handleResize = () => setIsMobileScreen(getScreenDimensions().isMobileScreen);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const methods = useForm<IFormInput>({
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  const [contactForm, { loading }] = useMutation(CONTACT_ADMIN, {
    onCompleted: (data) => {
      success(data.sendContactMail);
      reset();
    },
    onError: (err) => {
      error(err.message);
    },
  });

  const onSubmit = (data: IFormInput) => {
    contactForm({
      variables: {
        input: {
          name: data.name,
          email: data.email,
          phoneNumber: formatPhoneNumber(data.phoneNumber),
          message: data.message,
          adminInfo: {
            email: rawData?.[0]?.Emails,
            phoneNumber: rawData?.[0]?.[PHONE_NUMBER_TEXT],
            location: rawData?.[0]?.Address,
          },
        },
      },
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response: AxiosResponse<RawContactInfo[]> = await axios.get(
          `${katon_cms}/classroom_contacts`,
          {
            headers: {
              "X-Hash-Key": process.env.REACT_APP_KATON_SITE_API_KEY,
            },
          }
        );
        setRawData(response.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      } finally {
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Transform rawData to contactInfo structure
    if (rawData) {
      console.log("rawData is", rawData);
      const formattedContactInfo: ContactInfo[] = [
        {
          title: "Address",
          content: rawData[0].Address,
          icon: Location,
        },
        {
          title: "PhoneNumber",
          content: rawData[0][PHONE_NUMBER_TEXT].replace(/<br>/g, "").trim(),
          icon: Phone,
        },
        {
          title: "Emails",
          content: rawData[0].Emails,
          icon: Envelope,
        },
      ];
      console.log("formatted data", formattedContactInfo);
      setContactInfo(formattedContactInfo);
    }
    // Set the formatted data into the state
  }, [Envelope, Location, Phone, rawData]);
  
  
  const handleBackIcon = () => {
    navigate('/');
  }

  return (
    <div className="support-main-div" style={{height: loader ? '100%' : ''}}>
      <div className="supportHeadingContainer">
          <img alt="backIcon" src={LegalBackIcon} className="supportBackIconStyle" onClick={handleBackIcon}/>
          <div className="heading">Contact Information</div>
      </div>
      {loader ? (
        <div style={{ minWidth: "100%", minHeight: "100%" }}>
          <Loader />
        </div>
      ) : (
        <div className="support-container">
          {/* {contactInfo.length > 0 && ( */}
          <div className="details-container">
            {contactInfo.length > 0 &&
              contactInfo.map(({ title, content, icon }, index) => (
                <div className="container" key={index}>
                  <div className="symbol">
                    <img src={icon} alt={`${title} icon`} />
                  </div>
                  <div className="content-div">
                    {content.split("\r\n").map((line, idx) => (
                      <span key={idx}>{line}</span>
                    ))}
                  </div>
                </div>
              ))}
          </div>
          {/* )} */}
          <div className="form-div">
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} className="form">
                <CustomTextField
                  label="Your Name"
                  error={!!errors.name}
                  helperText={errors.name?.message || ""}
                  name="name"
                />
                <div
                  style={{ display: "flex", flexDirection: "row", gap: "30px" }}
                  className="numberEmailContainer"
                >
                  <CustomTextField
                    label={PHONE_NUMBER_TEXT}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message || ""}
                    name="phoneNumber"
                    pattern={/^\d{10}$/}
                  />
                  <CustomTextField
                    label="E-mail"
                    error={!!errors.email}
                    helperText={errors.email?.message || ""}
                    name="email"
                    pattern={/^\d{10}$/}
                  />
                </div>
                <StandardTextarea
                  label="Message"
                  error={!!errors.message}
                  helperText={errors.message?.message || ""}
                  name="message"
                />
                <div className="cta">
                  <ButtonStyle
                    disableButton={loading}
                    text="Send"
                    type="submit"
                    width="130px"
                  />
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
